import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useT } from '../i18n'
import { appearanceI18n } from '../i18n/shared/appearance'
import { saveAppearancePreference } from '../lib/appearance-save'
import { USE_REAL_API } from '../lib/api'
import type { AppearancePreference } from '../lib/appearance'
import { APPEARANCE_ENABLED, useAppearanceStore } from '../store/appearance'
import { PexIcon } from './icons/PexIcon'

const CHOICES: AppearancePreference[] = ['system', 'light', 'dark']

export function AppearanceMenu() {
  const t = useT(appearanceI18n)
  const [open, setOpen] = useState(false)
  const preference = useAppearanceStore((s) => s.preference)
  const accountUserId = useAppearanceStore((s) => s.accountUserId)
  const saving = useAppearanceStore((s) => s.saving)
  const error = useAppearanceStore((s) => s.error)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<Record<AppearancePreference, HTMLButtonElement | null>>({
    system: null,
    light: null,
    dark: null,
  })
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!open) return
    optionRefs.current[preference]?.focus()
    const onOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onOutside)
    return () => document.removeEventListener('pointerdown', onOutside)
  }, [open, preference])

  if (!APPEARANCE_ENABLED) return null

  const closeAndRestore = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const select = async (next: AppearancePreference) => {
    if (saving) return
    const saved = await saveAppearancePreference(next)
    if (mountedRef.current && saved) closeAndRestore()
  }

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closeAndRestore()
      return
    }
    if (event.key === 'Tab') {
      setOpen(false)
      return
    }
    const current = CHOICES.findIndex((choice) => optionRefs.current[choice] === document.activeElement)
    let next: number
    if (event.key === 'ArrowDown') next = (current + 1) % CHOICES.length
    else if (event.key === 'ArrowUp') next = (current + CHOICES.length - 1) % CHOICES.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = CHOICES.length - 1
    else return
    event.preventDefault()
    optionRefs.current[CHOICES[next]]?.focus()
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={t.currentChoice(t.choices[preference])}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={USE_REAL_API && !accountUserId}
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[var(--radius-control)] text-[var(--topbar-fg)] hover:bg-[var(--topbar-placeholder-bg)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:opacity-50"
      >
        <PexIcon name="appearance" size="md" tone="topbar" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t.title}
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-[var(--z-dropdown)] mt-2 w-56 max-w-[calc(100vw-2rem)] rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-1.5 text-sm text-[var(--color-text)] shadow-[var(--shadow-float)]"
        >
          {CHOICES.map((choice) => (
            <button
              key={choice}
              ref={(node) => { optionRefs.current[choice] = node }}
              type="button"
              role="menuitemradio"
              aria-label={t.choices[choice]}
              aria-checked={preference === choice}
              tabIndex={-1}
              aria-disabled={saving}
              onClick={() => { void select(choice) }}
              className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md px-3 text-left hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] aria-disabled:opacity-50"
            >
              <span>{t.choices[choice]}</span>
              {preference === choice && <PexIcon name="check" size="xs" tone="inherit" />}
            </button>
          ))}
          {saving && <p role="status" className="px-3 py-2 text-xs text-[var(--color-text-muted)]">{t.saving}</p>}
          {error && <p role="alert" className="px-3 py-2 text-xs text-[var(--color-danger)]">{error === 'save_failed' ? t.saveFailed : t.signInRequired}</p>}
        </div>
      )}
    </div>
  )
}
